import React, { FC, useEffect, useMemo, useState } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import DeleteIcon from '@material-ui/icons/Delete';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import AddIcon from '@material-ui/icons/Add';
import { KeyboardDatePicker } from '@material-ui/pickers';

import { luxon } from 'services/luxon';
import RepairPhotoUpload from './job-repairs-sheet-repair-photo-upload';
import RepairContractorDialog from './job-repairs-sheet-repair-contractor';
import { useStyles } from '../views/job-repairs-sheet-styles';
import { Repair, RepairPayment, Image } from '../views';
import { axiosInstance } from 'services/http';
import { extractErrorMessage } from 'utils';
import { Show } from 'components/Show';
import { getAddContractorsBlocked } from 'modules/master-sheet-v2/utils/getAddContractorsBlocked';
import {
  JobRepairEditBatchDto,
  JobRepairNoteDto,
  JobRepairNoteType,
  SubdivisionDto,
} from '@vatos-pas/common';
import { findOneSubdivision } from 'services/jobs';
import { PermissionsProvider } from 'providers/permissionsProvider';
import { CAN_MANAGE_HO_SERVICE_REPAIR } from 'providers/permissions';
import { canEditHOServiceRepair } from 'modules/master-sheet-v2/utils/canEditHOServiceRepair';
import NotesManagement from 'modules/master-sheet-v2/components/NotesManagement';

type RepairDialogProps = {
  repair: Repair;
  handleConfirm(): void;
  handleClose(): void;
  open: boolean;
};

const getPayment = (repairPayment: RepairPayment) => {
  const {
    approvedHours,
    estimatedHours,
    extraHours,
    fixedAmount,
    repairPaymentType,
    calculateCost,
  } = repairPayment;

  let type =
    // If it has `approvedHours` and doesn't have `estimatedHours`
    // then this record was created outside the bumpout timesheet flow, regardless of current subdivision flag.
    approvedHours && !estimatedHours
      ? 'Hours'
      : estimatedHours
      ? 'Estimated Hours'
      : extraHours
      ? 'Extra Hours'
      : fixedAmount
      ? 'Fixed Amount $'
      : 'Per Day';
  if (repairPaymentType === 'Percentage') {
    type = 'Calculated Cost';
  }

  let amount =
    approvedHours && !estimatedHours
      ? approvedHours
      : estimatedHours
      ? estimatedHours
      : extraHours
      ? extraHours
      : fixedAmount
      ? fixedAmount
      : '';
  if (repairPaymentType === 'Percentage') {
    amount = calculateCost;
  }

  return { type, amount };
};

const validateFields = (fields: {
  repairDate: Date;
}): Record<string, boolean> => {
  const errors: Record<string, boolean> = {};

  if (!fields.repairDate) {
    errors['repairDate'] = true;
  }

  return errors;
};

export const RepairDialog: FC<RepairDialogProps> = ({
  open,
  handleClose,
  handleConfirm,
  repair,
}) => {
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { hasPermission } = PermissionsProvider.useContainer();
  const canManageHOService = hasPermission(CAN_MANAGE_HO_SERVICE_REPAIR);
  const isHOServiceBlocked = !canEditHOServiceRepair(repair?.repairType, canManageHOService);

  const [saving, setSaving] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [addContractorModalOpen, setAddContractorModalOpen] = useState(false);

  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [notes, setNotes] = useState('');
  const [allNotes, setAllNotes] = useState<JobRepairNoteDto[]>([]);
  const [repairDate, setRepairDate] = useState(new Date(repair.repairDate));
  const [photos, setPhotos] = useState<Image[]>([]);
  const [photosStaged, setPhotosStaged] = useState<any>([]);
  const [payments, setPayments] = useState<RepairPayment[]>([]);
  const [deletedPayments, setDeletedPayments] = useState<string[]>([]);
  const [subdivision, setSubdivision] = useState<SubdivisionDto | null>(null);

  const isAddContractorsBlocked = getAddContractorsBlocked(repair);

  const classes = useStyles();

  // Fetch notes when modal opens
  useEffect(() => {
    const fetchNotes = async () => {
      if (open && repair?.id) {
        try {
          const notesResponse = await dataProvider.getList<JobRepairNoteDto>(
            'job-repair-note',
            {
              filter: {
                jobRepairId: repair.id,
                'jobRepairNoteType||$in': [
                  JobRepairNoteType.NOTE_BUMPOUT,
                  JobRepairNoteType.DESCRIPTION,
                ],
              },
              pagination: { page: 1, perPage: 1000 },
              sort: { field: 'createdAt', order: 'DESC' },
            },
          );

          setAllNotes(notesResponse.data);

          // Pre-fill the notes field with the most recent DESCRIPTION note
          const latestDescription = notesResponse.data.find(
            note => note.jobRepairNoteType === JobRepairNoteType.DESCRIPTION,
          );
          setNotes(latestDescription?.text || '');
        } catch (err) {
          notify('Failed to load notes', 'error');
        }
      }
    };

    fetchNotes();
  }, [open, repair?.id]);

  const loadPayments = () => {
    const list = repair.repairPayments.map(
      ({
        id,
        approvedHours,
        contractorId,
        repairFormulaId,
        extraHours,
        fixedAmount,
        estimatedHours,
        contractor,
        repairResourceType,
        windowStartTime,
        windowEndTime,
        calculateCost,
        repairPaymentType,
      }) => ({
        id,
        contractorId,
        approvedHours,
        repairFormulaId,
        extraHours,
        fixedAmount,
        estimatedHours,
        contractor,
        repairResourceType,
        windowStartTime,
        windowEndTime,
        calculateCost,
        repairPaymentType,
      }),
    );

    setPayments(list);
  };

  const loadPhotos = async () => {
    try {
      const { id } = repair;

      // Load photos separately since they're no longer included in job-repair response
      const photosResponse = await dataProvider.getList<any>(
        'job-repair-photo',
        {
          filter: {
            jobRepairId: id,
          },
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'createdAt', order: 'ASC' },
        },
      );

      setPhotos(photosResponse.data || []);
    } catch (error) {
      notify('Failed to load repair photos', 'error');
    }
  };

  const stagePhoto = (fileName: any) =>
    setPhotosStaged([...photosStaged, fileName]);

  const onDeleteContractorClick = (payment: RepairPayment, index: number) => {
    const newPayments = [...payments];
    newPayments.splice(index, 1);
    setPayments(newPayments);

    if (payment.id) {
      setDeletedPayments(payments => [...payments, payment.id]);
    }
  };

  const onAddContractorClick = () => setAddContractorModalOpen(true);

  const handleUploadClick = () => {
    setUploadModalOpen(true);
  };

  const onConfirmClick = async () => {
    try {
      const newErrors = validateFields({ repairDate });
      setErrors(newErrors);
      if (Object.keys(newErrors).length > 0) return;

      setSaving(true);
      const { id } = repair;

      const noteChanged =
        // Loose equality to check for both `null` and `undefined`
        (currentRepairNote == undefined && !!notes) ||
        (currentRepairNote != undefined && notes !== currentRepairNote.text);

      const insert = payments
        .filter(payment => !payment.id)
        .map(payment => {
          const {
            repairFormulaId,
            contractorId,
            estimatedHours,
            fixedAmount,
            extraHours,
            regionTimeWindowId,
          } = payment;
          return {
            repairFormulaId,
            contractorId,
            estimatedHours: estimatedHours ?? undefined,
            fixedAmount: fixedAmount ?? undefined,
            extraHours: extraHours ?? undefined,
            regionTimeWindowId: regionTimeWindowId ?? undefined,
          };
        });

      // Photos, note, payments and date used to be four requests; the API
      // now commits them together (WP11).
      const hasPaymentChanges = insert.length > 0 || deletedPayments.length > 0;
      const payload: JobRepairEditBatchDto = {
        repairDate: repairDate.toISOString(),
        ...(hasPaymentChanges && {
          repairPayments: { insert, delete: deletedPayments },
        }),
        ...(noteChanged && { descriptionNote: notes.trim() }),
        ...(photosStaged.length > 0 && {
          photos: photosStaged.map((photo: any) => ({
            fileName: photo.fileName,
            repairPhotoType: 'Photo',
          })),
        }),
      };

      await axiosInstance.patch(`job-repair/${id}/edit-batch`, payload);

      handleClose();
      handleConfirm();
    } catch (error) {
      setSaving(false);
      notify(extractErrorMessage(error, 'Failed to save the repair'), 'error');
    }
  };

  const currentRepairNote = useMemo(() => {
    return allNotes.find(
      note => note.jobRepairNoteType === JobRepairNoteType.DESCRIPTION,
    );
  }, [allNotes]);

  const hasChanges = useMemo(() => {
    const hasRepairDateChange = !luxon.isSameDay(
      luxon.date(repair.repairDate),
      luxon.date(repairDate),
    );
    const hasNewContractors = payments.some(payment => !payment.id);
    const hasDeletedContractors = deletedPayments.length > 0;
    const hasNotesChanges = currentRepairNote?.text !== notes;

    return (
      hasRepairDateChange ||
      hasNotesChanges ||
      hasNewContractors ||
      hasDeletedContractors
    );
  }, [
    repair,
    repairDate,
    currentRepairNote?.text,
    notes,
    deletedPayments,
    payments,
  ]);

  const loadSubdivision = async () => {
    const subdivision = await findOneSubdivision(
      dataProvider,
      repair.subdivisionId,
    );

    if (subdivision) {
      setSubdivision(subdivision);
    }
  };

  useEffect(() => {
    loadPhotos();
    loadPayments();
    loadSubdivision();
  }, [repair]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title-"
      aria-describedby="alert-dialog-description9"
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle id="alert-dialog-ti9tle">Repair</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-desc9ription">
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            className={classes.info}
          >
            <Box flex={1}>
              <Typography className={classes.label}>Repair Type</Typography>
              <Typography>{repair.repairTypeDescription}</Typography>
            </Box>
            <Box flex={2} mt="-8px">
              <KeyboardDatePicker
                disableToolbar
                variant="inline"
                format="MM/dd/yyyy"
                margin="normal"
                id="date-picker-inline"
                label="PO Request Date"
                fullWidth
                error={errors.repairDate}
                value={repairDate}
                onChange={selected => {
                  if (!selected) return;
                  setRepairDate(selected.toJSDate());
                }}
                KeyboardButtonProps={{ 'aria-label': 'change date' }}
              />
            </Box>
          </Box>
          <Box
            display="flex"
            justifyContent="space-between"
            my={2}
            className={classes.info}
          >
            <Box flex={2}>
              <Typography className={classes.label}>Subdivision</Typography>
              <Typography>{repair.subdivisionName || '-'}</Typography>
            </Box>
            <Box flex={1}>
              <Typography className={classes.label}>Lot</Typography>
              <Typography>{repair.jobLot?.padStart(4, '0') || '-'}</Typography>
            </Box>
            <Box flex={1}>
              <Typography className={classes.label}>Building #</Typography>
              <Typography>
                {repair.buildingNumber?.padStart(3, '0') || '-'}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" alignItems="center">
            <Box
              display="flex"
              justifyContent="space-between"
              className={classes.info}
            >
              <Box>
                <Typography className={classes.label}>PO Total $</Typography>
                <Typography>{repair.poTotal}</Typography>
              </Box>
            </Box>
            <Box justifyContent="flex-end" display="flex" flex={1} gridGap={8}>
              <IconButton onClick={handleUploadClick}>
                <CloudUploadIcon className={classes.iconsUpload} />
              </IconButton>
            </Box>
          </Box>
          <Box display="flex" alignItems="center" mt={2}>
            <NotesManagement
              enableHistory
              value={notes}
              onChange={event => setNotes(event.target.value)}
              jobRepairId={repair?.id}
              preloadedNotes={allNotes}
            />
          </Box>
          <Box className={classes.table} mt={2}>
            <Box className={classes.tableHeader}>
              <Typography>Contractors</Typography>
              {!isHOServiceBlocked && (
                <IconButton
                  style={{
                    padding: 0,
                  }}
                  disabled={isAddContractorsBlocked}
                  onClick={onAddContractorClick}
                >
                  <AddIcon
                    className={
                      isAddContractorsBlocked
                        ? classes.iconAddDisabled
                        : classes.iconAdd
                    }
                  />
                </IconButton>
              )}
            </Box>
            {payments.map((payment, i) => {
              const { amount, type } = getPayment(payment);

              return (
                <Box
                  key={`repair-payment-${i.toString()}`}
                  display="flex"
                  alignItems="flex-start"
                  justifyContent="space-between"
                  gridGap={16}
                  className={classes.tableLine}
                >
                  {!isHOServiceBlocked && (
                    <Box onClick={() => onDeleteContractorClick(payment, i)}>
                      <DeleteIcon className={classes.iconDelete} />
                    </Box>
                  )}
                  <Box flex={2}>
                    <Typography className={classes.label}>Resource</Typography>
                    <Typography>{payment.repairResourceType}</Typography>
                  </Box>
                  <Box flex={4}>
                    <Typography className={classes.label}>
                      Contractor
                    </Typography>
                    <Typography>{payment.contractor?.name}</Typography>
                  </Box>
                  <Box flex={1}>
                    <Typography className={classes.label}>{type}</Typography>
                    <Typography>{amount}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
          <Show condition={isAddContractorsBlocked}>
            <Typography
              style={{
                marginTop: 8,
              }}
            >
              Contractor is not able to be assigned until the PO has been
              released.
            </Typography>
          </Show>
        </DialogContentText>
        <Box display="flex" justifyContent="space-between" mt={4} mb={2}>
          <Button
            color="default"
            variant="contained"
            className={classes.buttonRepairsCancel}
            onClick={handleClose}
          >
            {isHOServiceBlocked ? 'Close' : 'Cancel'}
          </Button>
          {!isHOServiceBlocked && (
            <Button
              variant="contained"
              color="primary"
              className={classes.buttonRepairs}
              onClick={onConfirmClick}
              disabled={saving || !hasChanges}
            >
              Confirm
            </Button>
          )}
        </Box>
      </DialogContent>
      {uploadModalOpen && (
        <RepairPhotoUpload
          open
          handleClose={() => setUploadModalOpen(false)}
          setPhotosStaged={stagePhoto}
          photosStaged={photosStaged}
          photos={photos}
        />
      )}
      {addContractorModalOpen && (
        <RepairContractorDialog
          open
          repair={repair}
          isBumpoutTimesheetFlow={!!subdivision?.reqBumpoutTimesheet}
          handleClose={() => setAddContractorModalOpen(false)}
          handleConfirm={newPayment =>
            setPayments(oldPayments => [...oldPayments, newPayment])
          }
        />
      )}
    </Dialog>
  );
};

export default RepairDialog;
